import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  Avatar,
  Button,
  Chip,
  Icon,
  IconButton,
  Menu,
  RoleChip,
  Segmented,
  useToast,
} from '@w7/ui'
import { NAV } from './nav'
import { useSession } from './queries'
import { useDateRange, RANGE_PRESETS } from './range'
import { ROLE_LABEL } from '@/data/types'
import { money, moneyCompact } from '@/lib/format'
import { availableToGive } from '@/lib/book'
import { isDemoSession, logout, claims } from '@/auth/oidc'
import { SearchTrigger } from '@/features/search'

const RAIL_COLLAPSED_KEY = 'w7.rail.collapsed'

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * The authenticated shell.
 *
 * Rail is viewport-locked (does not scroll with page content). The nav list
 * scrolls only when items overflow — typical on short viewports. Desktop can
 * collapse to icon-only with tooltips; preference persists in localStorage.
 */

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [railOpen, setRailOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    setCollapsed(readCollapsedPreference())
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* private mode / blocked storage — preference is session-only */
    }
  }, [collapsed])

  // Any navigation closes the mobile rail — leaving it open over the new page
  // is the single most common phone-nav bug.
  useEffect(() => setRailOpen(false), [location.pathname])

  const toggleCollapsed = () => setCollapsed((value) => !value)

  return (
    <div className="w7-app">
      <a href="#main" className="w7-skip">
        Skip to content
      </a>

      <aside
        className="w7-rail"
        data-open={railOpen ? 'true' : undefined}
        data-collapsed={collapsed ? 'true' : undefined}
        aria-label="Console navigation"
      >
        <div className="w7-rail-head">
          <Link to="/" className="w7-logo" aria-label="W7 agent console — overview">
            <span>w</span>
            <b>7</b>
            <em>agent console</em>
          </Link>
          <IconButton
            name="menu"
            label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="w7-rail-collapse"
            onClick={toggleCollapsed}
          />
          <IconButton
            name="close"
            label="Close menu"
            className="w7-rail-close"
            onClick={() => setRailOpen(false)}
          />
        </div>

        <nav className="w7-rail-scroll" aria-label="Console sections">
          {NAV.map((group) => (
            <div key={group.id} className="w7-navgroup">
              <p className="w7-navgroup-label">{group.label}</p>
              {group.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.to === '/'}
                  className="w7-navitem"
                  title={collapsed ? `${item.label} — ${item.description}` : item.description}
                  aria-label={collapsed ? item.label : undefined}
                >
                  <span className="w7-navitem-icon" aria-hidden="true">
                    <Icon name={item.icon} size={18} />
                  </span>
                  <span className="w7-navitem-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="w7-rail-foot">
          <p className="w7-micro">{isDemoSession() ? 'Fixture data' : 'Live session'}</p>
          <p className="w7-dim" style={{ fontSize: 11, lineHeight: 1.45 }}>
            {isDemoSession()
              ? 'Every figure here is generated, not from the ledger. Wired to live services this becomes real without a UI change.'
              : 'Figures come from hierarchy and analytics for your agentId. Demo login still uses fixtures.'}
          </p>
        </div>
      </aside>

      {/* Its OWN scrim class, not the modal one. The shared `w7-scrim` sits at
          z-index 100 so it can cover a drawer at 101; the mobile rail is at 60,
          so reusing it put the scrim ON TOP of the open menu and swallowed every
          tap — the menu opened and then did nothing. */}
      {railOpen ? (
        <div className="w7-scrim w7-scrim-nav" onClick={() => setRailOpen(false)} />
      ) : null}

      <div className="w7-main">
        <Topbar onOpenRail={() => setRailOpen(true)} />
        <main className="w7-content" id="main">
          {children}
        </main>
      </div>
    </div>
  )
}

/**
 * Routes that carry their own date range in their own URL.
 *
 * The five reports each render a `DateRangePicker` inside their filter bar, and
 * that picker — not the shell — is what their query reads. Leaving the shell's
 * Segmented on screen beside it puts two range controls on one page that
 * disagree: pick "Last week" on a report and the topbar still reads 30D while
 * the table underneath shows Mon–Sun. So the shell control stands down where it
 * is not the answer. It still seeds a report that arrives with no range in its
 * URL, which is the one job it genuinely has there.
 */
const OWNS_ITS_RANGE = /^\/reports(\/|$)/

function Topbar({ onOpenRail }: { onOpenRail: () => void }) {
  const { data: session } = useSession()
  const { range, preset, setPreset } = useDateRange()
  const { pathname } = useLocation()
  const toast = useToast()
  const showRange = !OWNS_ITS_RANGE.test(pathname)

  const account = session?.account
  const headroom = account ? availableToGive(account.balance, account.exposure) : null
  const demo = isDemoSession()
  const who = claims()
  const signedIn = Boolean(who || demo)
  const signedInAs =
    who?.preferredUsername || who?.email || (demo ? 'demo' : account?.username) || 'Signed in'
  const roleLabel = account ? ROLE_LABEL[account.role] : undefined

  const notReady = (label: string) =>
    toast.push({
      tone: 'info',
      message: `${label} is not available yet`,
      detail: 'It will open against Keycloak account management once public identity is live.',
    })

  return (
    <header className="w7-topbar">
      <IconButton name="menu" label="Open menu" className="w7-rail-toggle" onClick={onOpenRail} />

      {showRange ? (
        <div className="w7-topbar-dates">
          <Segmented
            label="Date range"
            value={preset}
            onChange={setPreset}
            items={RANGE_PRESETS.map((p) => ({ id: p.id, label: p.short }))}
          />
          <span className="w7-topbar-range w7-dim">{range.label}</span>
        </div>
      ) : null}

      {/* The palette's discoverability. Placed before the spacer so it sits with
          the workspace controls rather than the account trail — it is a way to
          get somewhere, not a setting. Collapses to the magnifier below 900px. */}
      <SearchTrigger />

      <span className="w7-spacer" />

      <div className="w7-topbar-trail">
        <div
          className="w7-wallet"
          title={
            account
              ? `Balance ${money(account.balance)} · Exposure ${money(account.exposure)}`
              : undefined
          }
        >
          <Icon name="wallet" size={16} />
          <span>
            <b>{headroom === null ? '—' : moneyCompact(headroom)}</b>
            <em>free to give</em>
          </span>
        </div>

        {demo ? (
          <Chip
            tone="warn"
            size="sm"
            icon="info"
            className="w7-demochip"
            title="Fixture world — not ledger-backed"
          >
            <span className="w7-demochip-text">Demo data</span>
          </Chip>
        ) : (
          <Chip
            tone="profit"
            size="sm"
            icon="live"
            className="w7-demochip"
            title="Signed in — live Keycloak session"
          >
            <span className="w7-demochip-text">Live session</span>
          </Chip>
        )}

        {signedIn || account ? (
          <Menu
            label="Account"
            align="end"
            trigger={<Avatar name={signedInAs} />}
            header={
              <div className="w7-menu-who">
                <Avatar name={signedInAs} size="sm" />
                <div className="w7-menu-who-text">
                  <span className="w7-menu-who-name w7-truncate" title={signedInAs}>
                    {signedInAs}
                  </span>
                  {roleLabel ? <span className="w7-menu-who-role">{roleLabel}</span> : null}
                </div>
              </div>
            }
            items={[
              {
                id: 'password',
                label: 'Change password',
                icon: 'lock',
                onSelect: () => notReady('Change password'),
              },
              {
                id: '2fa',
                label: 'Two-factor authentication',
                icon: 'shield',
                onSelect: () => {
                  // Hard navigate so a stale SPA shell cannot keep an old Keycloak assign.
                  window.location.assign(`${window.location.origin}/settings/security`)
                },
              },
              {
                id: 'prefs',
                label: 'Preferences',
                icon: 'sliders',
                onSelect: () => notReady('Preferences'),
              },
              'separator',
              {
                id: 'out',
                label: 'Sign out',
                icon: 'logout',
                tone: 'danger',
                onSelect: () => logout(),
              },
            ]}
          />
        ) : null}
      </div>
    </header>
  )
}

/** Identity block reused by the downline table and the member header. */
export function AccountCell({
  username,
  displayName,
  role,
  to,
  meta,
}: {
  username: string
  displayName: string
  role: string
  to?: string
  meta?: React.ReactNode
}) {
  const body = (
    <>
      <Avatar name={displayName || username} size="sm" />
      <span style={{ minWidth: 0 }}>
        <span className="w7-row" style={{ gap: 6 }}>
          <span className="w7-truncate" style={{ fontWeight: 600 }}>
            {username}
          </span>
          <RoleChip role={role} label={ROLE_LABEL[role as keyof typeof ROLE_LABEL] ?? role} />
        </span>
        <span className="w7-dim w7-truncate" style={{ display: 'block', fontSize: 11.5 }}>
          {meta ?? displayName}
        </span>
      </span>
    </>
  )

  return to ? (
    <Link to={to} className="w7-identity">
      {body}
    </Link>
  ) : (
    <span className="w7-identity">{body}</span>
  )
}

export { Button }
